import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ExternalLink, TriangleAlert } from "lucide-react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  ConfirmModal,
  CopyableId,
  DescriptionItem,
  DescriptionList,
  EntityDrawer,
  Skeleton,
  Switch,
  type BadgeVariant,
} from "@fox1ck-org/ui";
import { toast } from "@fox1ck-org/ui/toast";
import { BindingMode, BindingStatus, TrackingDomainStatus, type TrackingDomain } from "@gen/tracker/v1/tracking_domain_pb";

import { WriteOnly } from "@/components/auth/write-only";
import { DomainEntryPoints } from "@/components/domains/entry-points";
import { STATE_TONE, bindingErrorText, domainState, isProviderUnavailable, stateHintKey, stateLabelKey } from "@/lib/domain-binding";
import { plural, useT, type TKey } from "@/lib/i18n";
import { formatInt } from "@/lib/format";
import { carryPeriod } from "@/lib/route-tab";
import { formatStamp } from "@/lib/utils";
import { RoleGate } from "@/components/auth/role-gate";
import { useCanWrite, useTrackerRoles } from "@/lib/permissions";
import {
  useDeleteTrackingDomain,
  useDomainCampaigns,
  useSetDefaultTrackingDomain,
  useSyncTrackingDomain,
  useUpdateTrackingDomain,
} from "@/lib/services/tracking-domain-service";

const CAMPAIGN_STATUS: Record<string, { variant: BadgeVariant; label: TKey }> = {
  ACTIVE: { variant: "ok", label: "lib.status.active" },
  PAUSED: { variant: "warn", label: "lib.status.paused" },
  DRAFT: { variant: "neutral", label: "lib.status.draft" },
};

/** A Connect failure the buyer can act on, or the screen's own sentence. */
function failureText(t: ReturnType<typeof useT>, err: unknown, fallback: string): string {
  if (isProviderUnavailable(err)) return t("domains.unavailable.body");
  return err instanceof Error && err.message ? `${fallback}: ${err.message}` : fallback;
}

/**
 * Домен (доска DomainDrawer): the shape the domain is in and what that costs
 * a campaign — the provider's own reason when it broke — the records the fqdn
 * must carry, how it is connected, and the campaigns whose ad link lives on
 * it. «Проверить сейчас» asks the provider at once instead of waiting for the
 * minute sync; «Отвязать» removes the binding (the provider keeps the domain).
 * Nothing here changes a domain until the person presses a button.
 */
export function DomainDrawer({ domain, onOpenChange, tz }: { domain: TrackingDomain | undefined; onOpenChange: (open: boolean) => void; tz: string }) {
  const t = useT();
  const canWrite = useCanWrite();
  // DeleteTrackingDomain is RequireManage.
  const canManage = useTrackerRoles().canManage;
  const sync = useSyncTrackingDomain();
  const remove = useDeleteTrackingDomain();
  const update = useUpdateTrackingDomain();
  const setDefault = useSetDefaultTrackingDomain();
  const campaigns = useDomainCampaigns(domain?.id);
  const [unbinding, setUnbinding] = useState(false);
  const carried = carryPeriod(useLocation().search);

  const state = domain ? domainState(domain) : "unknown";
  const reason = domain ? bindingErrorText(domain) : "";
  const n = domain?.campaigns ?? 0;

  const check = () => {
    if (!domain) return;
    sync.mutate(domain.id, {
      onSuccess: (d) => {
        if (d.bindingStatus === BindingStatus.BOUND) toast.success(t("domains.check.bound", { fqdn: d.fqdn }));
        else {
          const why = bindingErrorText(d);
          toast.warning(why ? t("domains.check.waiting", { reason: why }) : t("domains.check.waitingPlain"));
        }
      },
      onError: (e) => toast.error(failureText(t, e, t("domains.check.failed"))),
    });
  };

  return (
    <>
      <EntityDrawer
        open={!!domain}
        onOpenChange={onOpenChange}
        width="xl"
        title={domain?.fqdn ?? ""}
        primaryAction={domain ? { label: t("ops.dom.checkNow"), onClick: check, loading: sync.isPending, loadingLabel: t("domains.action.checking") } : undefined}
        destructiveAction={domain && canManage ? { label: t("ops.dom.unbind"), onClick: () => setUnbinding(true) } : undefined}
      >
        {domain ? (
          <div className="flex flex-col gap-5">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={STATE_TONE[state]} dot pulse={state === "waiting"}>
                {t(stateLabelKey(state))}
              </Badge>
              <Badge variant="outline">{n > 0 ? `${formatInt(n)} ${plural(t("word.campaigns"), n)}` : t("ops.dom.free")}</Badge>
              {domain.isDefault ? <Badge variant="primary">{t("domains.badge.default")}</Badge> : null}
            </div>

            {state !== "working" && state !== "disabled" ? (
              <Alert variant={state === "failed" ? "danger" : "warn"}>
                <TriangleAlert className="size-4" />
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <AlertTitle>{t(stateHintKey(state))}</AlertTitle>
                    <AlertDescription>
                      {reason ? <span className="block break-words font-mono text-xs">{reason}</span> : null}
                      {t("domains.blocked.body")}
                    </AlertDescription>
                  </div>
                  {domain.manageUrl ? (
                    <Button asChild variant="secondary" size="sm">
                      <a href={domain.manageUrl} target="_blank" rel="noreferrer">
                        {t("ops.dom.fixAt")} <ExternalLink className="size-3.5" />
                      </a>
                    </Button>
                  ) : null}
                </div>
              </Alert>
            ) : null}

            <section className="flex flex-col gap-2" aria-label={t("ops.dom.records")}>
              <div className="flex items-center">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("ops.dom.records")}</h3>
                <span className="ml-auto text-xs text-muted-foreground">
                  {domain.bindingCheckedAt ? t("ops.dom.checkedAt", { at: formatStamp(domain.bindingCheckedAt, tz) }) : t("domains.detail.checkedNever")}
                </span>
              </div>
              {domain.bindingRecords.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t("domains.record.none")}</p>
              ) : (
                <div className="overflow-hidden rounded-md border border-border">
                  {domain.bindingRecords.map((r, i) => (
                    <div key={`${r.type}-${r.name}-${i}`} className="grid grid-cols-[--spacing(16)_--spacing(24)_minmax(0,1fr)_auto] items-center gap-3 border-b border-border px-3 py-2 text-sm last:border-b-0">
                      <span className="font-mono text-xs">{r.type}</span>
                      <span className="truncate font-mono text-xs text-muted-foreground">{r.name}</span>
                      <CopyableId value={r.content} className="min-w-0 font-mono text-xs" />
                      <Badge variant="outline">
                        {r.proxied ? t("ops.dom.proxied") : t("ops.dom.dnsOnly")}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
              {domain.bindingProvider === "manual" && state !== "working" ? <p className="text-xs text-muted-foreground">{t("domains.steps.2")}</p> : null}
            </section>

            <section className="flex flex-col gap-2" aria-label={t("ops.dom.connection")}>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("ops.dom.connection")}</h3>
              <DescriptionList>
                <DescriptionItem
                  label={t("ops.dom.where")}
                  value={
                    domain.manageUrl ? (
                      <a href={domain.manageUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-primary">
                        {providerLabel(t, domain)} <ExternalLink className="size-3" />
                      </a>
                    ) : (
                      providerLabel(t, domain)
                    )
                  }
                />
                <DescriptionItem label={t("ops.dom.mode")} value={t(domain.bindingMode === BindingMode.OVERLAY ? "ops.dom.mode.overlay" : "ops.dom.mode.direct")} />
                {domain.bindingRef ? <DescriptionItem label={t("ops.dom.ref")} value={<CopyableId value={domain.bindingRef} className="font-mono text-xs" />} /> : null}
                <DescriptionItem label={t("ops.dom.added")} value={formatStamp(domain.createdAt, tz)} />
                <DescriptionItem
                  label={t("ops.dom.default")}
                  value={
                    domain.isDefault ? (
                      t("ops.dom.default.yes")
                    ) : canWrite ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="-ml-2"
                        disabled={setDefault.isPending}
                        onClick={() =>
                          setDefault.mutate(domain.id, {
                            onSuccess: () => toast.success(t("domains.default.done", { fqdn: domain.fqdn })),
                            onError: (e) => toast.error(failureText(t, e, t("domains.default.failed"))),
                          })
                        }
                      >
                        {t("domains.default.make")}
                      </Button>
                    ) : (
                      t("ops.dom.default.no")
                    )
                  }
                />
              </DescriptionList>
              <WriteOnly>
                <label className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2.5 text-sm">
                  <span className="flex flex-col">
                    {t("domains.serve.label")}
                    <span className="text-xs text-muted-foreground">{t("domains.serve.hint")}</span>
                  </span>
                  <Switch
                    checked={domain.status === TrackingDomainStatus.ACTIVE}
                    disabled={update.isPending}
                    onCheckedChange={(on) =>
                      update.mutate(
                        { id: domain.id, status: on ? TrackingDomainStatus.ACTIVE : TrackingDomainStatus.DISABLED },
                        { onError: () => toast.error(t("domains.serve.failed")) },
                      )
                    }
                  />
                </label>
              </WriteOnly>
              <RoleGate need="manage" label={t("ops.dom.unbind")} title={t("ops.dom.unbind")} className="self-start" />
            </section>

            <DomainEntryPoints domain={domain} />

            <section className="flex flex-col gap-2" aria-label={t("ops.dom.campaigns")}>
              <div className="flex items-center">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("ops.dom.campaigns")}</h3>
                <span className="ml-auto font-mono text-xs text-muted-foreground">{formatInt(n)}</span>
              </div>
              {campaigns.isPending ? (
                <Skeleton className="h-14 w-full" />
              ) : (campaigns.data ?? []).length === 0 ? (
                <p className="text-xs text-muted-foreground">{t("ops.dom.campaigns.none")}</p>
              ) : (
                <div className="overflow-hidden rounded-md border border-border">
                  {campaigns.data!.map((c) => {
                    const st = CAMPAIGN_STATUS[c.status] ?? CAMPAIGN_STATUS.DRAFT!;
                    return (
                      <div key={c.id} className="flex items-center gap-3 border-b border-border px-3 py-2.5 last:border-b-0">
                        <Badge variant={st.variant} dot>
                          {t(st.label)}
                        </Badge>
                        <div className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate text-sm">{c.name}</span>
                          <span className="truncate font-mono text-xs text-muted-foreground">
                            {domain.fqdn}/{c.publicPath}
                          </span>
                        </div>
                        <Button asChild variant="ghost" size="sm">
                          <Link to={`/campaigns/${c.id}${carried}`}>{t("ops.open")}</Link>
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        ) : null}
      </EntityDrawer>
      <ConfirmModal
        open={unbinding}
        onClose={() => setUnbinding(false)}
        tone="danger"
        loading={remove.isPending}
        title={t("ops.dom.unbind.title", { fqdn: domain?.fqdn ?? "" })}
        description={n > 0 ? t("ops.dom.unbind.used", { count: formatInt(n), campaigns: plural(t("word.campaigns"), n) }) : t("ops.dom.unbind.body")}
        confirmLabel={t("ops.dom.unbind")}
        onConfirm={() => {
          if (!domain) return;
          remove.mutate(domain.id, {
            onSuccess: () => {
              toast.success(t("domains.delete.done"));
              setUnbinding(false);
              onOpenChange(false);
            },
            onError: (e) => toast.error(failureText(t, e, t("domains.delete.failed"))),
          });
        }}
      />
    </>
  );
}

/** Who holds the domain, as a buyer says it: manual DNS, else the adapter's own name. Pure. */
export function providerLabel(t: ReturnType<typeof useT>, d: Pick<TrackingDomain, "bindingProvider" | "bindingProviderLabel">): string {
  if (d.bindingProvider === "manual") return t("ops.dom.provider.manual");
  return d.bindingProviderLabel || d.bindingProvider || "—";
}
